import React, { useState } from "react";
import { LogOut, UserCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Button } from "@/components/ui/button.jsx";
import UserProfileModal from "./UserProfileModal.jsx";

export default function UserProfileWidget({ compact = false }) {
  const {
    userFirstName,
    email,
    userRole,
    roles,
    profileUrl,
    userDetails,
    logout,
  } = useAuth();

  const [modalOpen, setModalOpen] = useState(false);

  const getInitials = (nameStr) => {
    if (!nameStr) return "U";
    const parts = nameStr.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return nameStr.slice(0, 2).toUpperCase();
  };

  const displayName =
    userDetails?.fullName ||
    userDetails?.name ||
    [userDetails?.firstName, userDetails?.lastName].filter(Boolean).join(" ") ||
    userFirstName ||
    (email ? email.split("@")[0] : "Staff Member");

  const avatarSrc = profileUrl || userDetails?.imageUrl || "";
  const roleTitle = userRole || roles?.[0] || "Staff";

  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="relative group rounded-full p-1 transition hover:ring-2 hover:ring-primary/40 focus:outline-none"
          title={`Logged in as ${displayName} (${roleTitle})`}
          aria-label="View user profile"
        >
          <Avatar className="h-9 w-9 border border-slate-200 shadow-sm">
            {avatarSrc ? (
              <AvatarImage src={avatarSrc} alt={displayName} className="object-cover" />
            ) : null}
            <AvatarFallback className="bg-gradient-to-br from-primary via-rose-600 to-amber-600 text-white font-bold text-xs">
              {getInitials(displayName)}
            </AvatarFallback>
          </Avatar>
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
        </button>

        <UserProfileModal open={modalOpen} onOpenChange={setModalOpen} />
      </>
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-2.5 shadow-sm transition-all hover:bg-slate-100/80 hover:border-slate-300/80">
        <div className="flex items-center gap-3">
          {/* Avatar button */}
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="relative shrink-0 group focus:outline-none"
            title="Click to view full user profile"
          >
            <Avatar className="h-10 w-10 border-2 border-white shadow-sm ring-1 ring-slate-200">
              {avatarSrc ? (
                <AvatarImage src={avatarSrc} alt={displayName} className="object-cover" />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-primary via-rose-600 to-amber-600 text-white font-bold text-sm">
                {getInitials(displayName)}
              </AvatarFallback>
            </Avatar>
            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
          </button>

          {/* User Details */}
          <div
            className="min-w-0 flex-1 cursor-pointer"
            onClick={() => setModalOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setModalOpen(true);
            }}
          >
            <div className="flex items-center gap-1.5">
              <p className="truncate text-xs font-bold text-slate-900 leading-tight">
                {displayName}
              </p>
            </div>

            <div className="flex items-center gap-1.5 mt-0.5">
              <Badge
                variant="secondary"
                className="bg-primary/10 text-primary border-transparent font-medium text-[10px] px-1.5 py-0 h-4 rounded-md"
              >
                {roleTitle}
              </Badge>
              {email && (
                <p className="truncate text-[11px] font-normal text-slate-500">
                  {email}
                </p>
              )}
            </div>
          </div>

          {/* Logout button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={logout}
            className="h-8 w-8 shrink-0 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Logout"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <UserProfileModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}
